import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import Spinner from 'components/Spinner'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import EscalationContactsBody from 'scenes/RecoveryAI/components/EscalationContactsBody'
import { successNotificationOverrides } from 'scenes/RecoveryAI/components/successNotificationOverrides'
import { useEscalationContactsController } from 'scenes/RecoveryAI/components/useEscalationContactsController'
import { useStyletron } from 'styletron-react'
import {
  ESCALATION_CONTACTS_SAVE_KEY,
  ESCALATION_NOTIFY_SAVE_KEY,
  WIZARD_CONTENT_MAX_WIDTH,
} from '../../constants'

// AI-883: the Help block was originally removed (L5) because this link
// pointed at a help article that was never written. Re-added per direct
// product decision, now pointing at the published article covering the
// Location Manager permission level (assigning it to a team member via the
// Dashboard) — confirmed live and current via Slack (#merchant-ops-internal,
// #product-requests both reference it for Location Manager access).
const HELP_LINK_URL =
  'https://help.thanx.com/hc/en-us/articles/17663808714903-Adding-team-members-and-updating-access-permissions-through-the-Dashboard'

// THREE bindings, all named at the call site. `t` points at this step's own
// namespace, so the banner copy — which lives under settings.escalation_contacts
// — must never be read through it: a mis-bound key renders its raw path into a
// merchant-facing banner, and nothing in CI catches that.
const t = buildTranslate('recovery_ai.wizard.escalation_routing')
const tEscalations = buildTranslate('recovery_ai.wizard.escalations')
const tContacts = buildTranslate('recovery_ai.settings.escalation_contacts')

// Namespaces the toggle's label id and both pickers' description ids (C1), so
// the wizard's copy of the body can co-exist with any other instance.
const INSTANCE_ID = 'wizard-escalations'

/**
 * The wizard's Escalations stage: the same recipient UI the Settings tab ships
 * (D1), over the same controller. Recipients are existing merchant users, never
 * free-typed addresses.
 *
 *   context: the step owns no data layer — only chrome, banners and the Retry.
 *   Writes are EAGER (L12/L27): one PUT per change, no Save button, controls
 *   disabled in flight. The two saveKeys let useActivationCompleteness, which runs
 *   in the shell above this step, hold Finalize closed while one of those writes is
 *   in flight or failed.
 *
 *   Why eager rather than staged-behind-Finalize: NOT because a Finalize-ordered
 *   write would land after onboarding_completed — ordering is ours to choose, and
 *   `if (!(await contacts.commit())) return` before submitStep would sequence it
 *   safely. The real reason is that the gate reads PERSISTED truth from the shell,
 *   above this step, and there is no channel for it to see a staged selection down
 *   here: fixedCacheKey shares mutation state, not draft state. Staging would mean
 *   lifting the recipient draft into WizardContext, which D1/L31 deliberately
 *   removed.
 *
 *   ACCEPTED LIMITATION (AI-968): a RE-ENTERING activated merchant mutates live
 *   routing on click — no Save to withhold, no confirmation, no undo, no audit
 *   row, and deselecting CLEARS the primary rather than changing it. Safe only
 *   while unreachable, so AI-968 BLOCKS enabling `recovery-ai` anywhere with real
 *   merchant data (`recovery-ai-setup-guide` gates the entry button, not this
 *   route — a bookmarked URL still lands here). Do not assume a backstop catches
 *   a cleared primary: notify_admins is false for every merchant (AI-969) and an
 *   empty recipient set sends nothing, silently.
 */
const EscalationRouting = () => {
  const [css] = useStyletron()
  const contacts = useEscalationContactsController({
    notifyMode: 'eager',
    recipientMode: 'eager',
    saveKeys: {
      contacts: ESCALATION_CONTACTS_SAVE_KEY,
      config: ESCALATION_NOTIFY_SAVE_KEY,
    },
  })

  const renderContent = () => {
    if (contacts.isLoading) {
      return (
        <div role="status" aria-label={tContacts('loading')}>
          <Spinner
            isLoading
            size="4x"
            className="text-center margin-top-huge"
          />
        </div>
      )
    }

    // A failed users FETCH leaves no recipients to choose from, so it is a hard
    // error — distinct from the demoted contacts 403/404 below, where the
    // pickers still render for review.
    if (contacts.isUsersError) {
      return (
        <div className={css({ textAlign: 'center' })} role="alert">
          <Text.BodyText3 color="cayenne50">
            {tContacts('fetch_error')}
          </Text.BodyText3>
        </div>
      )
    }

    return (
      <>
        {/* #3883: with the roles lookup unresolved, every RBAC admin demotes into
            ALL OTHER USERS with no badge. Non-blocking — the pickers still work. */}
        {contacts.adminGroupingUnavailable && (
          <div className="mb-m">
            <Notification kind="info" size="full">
              {tContacts('admin_grouping_unavailable')}
            </Notification>
          </div>
        )}
        {contacts.contactsUnavailable && (
          <div className="mb-m">
            <Notification kind="warning" size="full">
              {contacts.recipientsReadOnly
                ? tContacts('unloadable_notice')
                : tContacts('unavailable_notice')}
            </Notification>
          </div>
        )}
        {/* The two negative banners are mutually exclusive: a notify-only
            failure means the recipients DID land, so stacking the generic
            "couldn't save your escalation contacts" under it would contradict
            itself. uikit's Notification self-renders role="alert". */}
        {contacts.isSaveError && !contacts.isNotifyOnlySaveError && (
          <div className="mb-m">
            <Notification kind="negative" size="full">
              {tContacts('save_error')}
            </Notification>
            {/* There is no Save button on this step to retry with, so the
                recovery affordance rides beside the failure. Disabled while
                busy: this re-fires a REPLACE-not-merge PUT, where two in-flight
                writes can land out of order and persist the wrong set.
                Bound to `retry`, NOT commit(): commit's recipients branch is
                gated on recipientsDirty, so after an edit-then-revert it issues
                nothing and resolves true — a Try again that does nothing while
                the banner it sits under stays up and Finalize stays blocked. */}
            <div className="mt-s">
              <Button
                kind="secondary"
                onClick={contacts.retry}
                disabled={contacts.isBusy}
              >
                {tEscalations('retry_save')}
              </Button>
            </div>
          </div>
        )}
        {contacts.isNotifyOnlySaveError && (
          <div className="mb-m">
            <Notification kind="negative" size="full">
              {tContacts('notify_save_error')}
            </Notification>
          </div>
        )}
        {contacts.saveSuccess && !contacts.isSaveError && (
          <div className="mb-m">
            <Notification
              kind="positive"
              size="full"
              overrides={successNotificationOverrides}
            >
              {tContacts('save_success')}
            </Notification>
          </div>
        )}

        <EscalationContactsBody
          instanceId={INSTANCE_ID}
          users={contacts.users}
          primaryIds={contacts.primaryIds}
          onPrimaryChange={contacts.onPrimaryChange}
          additionalIds={contacts.additionalIds}
          onAdditionalChange={contacts.onAdditionalChange}
          notifyLocationManagers={contacts.notifyLocationManagers}
          onNotifyChange={contacts.onNotifyChange}
          canManage={contacts.canManage}
          isAdmin={contacts.isUserAdmin}
          isNotifySaving={contacts.isNotifySaving}
          isBusy={contacts.isBusy}
          // The EAGER surface takes the stricter of the controller's two
          // unavailability flags. The pane can honour `contactsUnavailable` by
          // disabling its Save and leaving the pickers live "for review" — here a
          // picker click IS the save, so the pane's state is unrepresentable and
          // leaving them live makes the notice above contradict the surface.
          // The notify toggle is unaffected (it reads isNotifySaving/canManage), which
          // keeps a contacts 403 escapable rather than dead-ending Finalize.
          recipientsReadOnly={
            contacts.recipientsReadOnly || contacts.contactsUnavailable
          }
          // L28: a same-tab <Link> would unmount WizardProvider and discard
          // session-only state (testingComplete) on the final step. The Settings
          // pane keeps its in-tab link — a deliberate surface difference.
          inviteAffordance="new-tab"
        />
      </>
    )
  }

  return (
    <div
      className={css({ maxWidth: WIZARD_CONTENT_MAX_WIDTH, margin: '0 auto' })}
    >
      <div className="d-flex flex-column mb-xl">
        <Text.Header3 bold>{t('title')}</Text.Header3>
        <Text.BodyText3 color="grey70" className="mt-xs">
          {t('description')}
        </Text.BodyText3>
      </div>

      {renderContent()}

      {/* Help — link to the location-manager setup guide. AI-883: re-added
          after being pulled as a stub, with the two bugs that ticket reported
          fixed at the root cause rather than restored as-is:
            - question-mark icon, not the old lightbulb (fa-lightbulb-o)
            - a plain external <a> wrapping Text.BodyText4 (the
              ThanxAIHelpLinks pattern), not a <Button kind="minimal"> — the
              Button didn't inherit BodyText4 sizing, which is what made the
              link render too large.
          Rendered unconditionally (like the header above), independent of
          contacts.isLoading/isUsersError: it's static help content, not tied
          to whether the recipient data loaded. */}
      <div
        className={`d-flex flex-column ${css({
          gap: '4px',
          marginTop: '32px',
        })}`}
      >
        <div className={`d-flex align-items-center ${css({ gap: '6px' })}`}>
          <i
            className={`fa fa-question-circle ${css({
              color: palette.grey70,
            })}`}
            aria-hidden="true"
          />
          <Text.BodyText4 bold color="grey70">
            {tEscalations('help_title')}
          </Text.BodyText4>
        </div>
        <a
          href={HELP_LINK_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={tEscalations('help_link_aria')}
        >
          <Text.BodyText4 color="belizeHole50">
            {tEscalations('help_link')}{' '}
            <i className="fa fa-external-link" aria-hidden="true" />
          </Text.BodyText4>
        </a>
      </div>
    </div>
  )
}

export default EscalationRouting
